import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { addDepartmentToBackend, updateDepartmentToBackend } from "app/services/department.service";
import MDBox from "components/MDBox";
import { validateDepartmentObject } from "utils/departmentValidationUtil";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  id: null,
  code: "",
  displayName: "",
  description: "",
  status: "ACTIVE",
};

// @since NEXT_VERSION
const AddDepartmentDialog = ({ open, setOpen, refreshDepartmentsHandler, editObject }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info");
  const [data, setData] = useState(obj);

  useEffect(() => {
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
    if (!open) return;
    if (editObject) {
      setData({
        id: parseInt(editObject.id, 10),
        code: editObject.code ?? "",
        displayName: editObject.displayName ?? "",
        description: editObject.description ?? "",
        status: editObject.status ?? "ACTIVE",
      });
    } else {
      setData(obj);
    }
  }, [open, editObject]);

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const closeAfterSuccess = () => {
    setTimeout(() => {
      setData(obj);
      setMessageOpen(false);
      setMessage("");
      setOpen(false);
      refreshDepartmentsHandler();
    }, 1000);
  };

  const saveDepartmentInBackend = async () => {
    // Upper-case here as well as on the server so what the user sees saved matches what is stored;
    // the unique index is case-insensitive either way.
    const payload = trimTextFields({ ...data, code: (data.code ?? "").trim().toUpperCase() });

    if (!validateDepartmentObject(payload)) {
      setErrorAction("One or more required fields for a Department are missing or invalid");
      return;
    }

    if (editObject) {
      await updateDepartmentToBackend(payload.id, payload)
        .then(() => {
          setSuccessAction("Department updated successfully");
          closeAfterSuccess();
        })
        .catch((error) => showBackendError(error, "Failed to update department. Please try again."));
    } else {
      await addDepartmentToBackend(payload)
        .then(() => {
          setSuccessAction("Department added successfully");
          closeAfterSuccess();
        })
        .catch((error) => showBackendError(error, "Failed to add department. Please try again."));
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>{editObject ? `Update Department` : `Add New Department`}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="dept_code"
            label="DeptCode"
            fullWidth
            variant="outlined"
            value={data?.code || ""}
            onBlur={trimOnBlur(setData, "code")}
            onChange={(e) => setData({ ...data, code: e.target.value.toUpperCase() })}
            helperText="*Required. Up to 20 characters, letters/digits/hyphen (e.g. ADA)"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="display_name"
            label="Display Name"
            fullWidth
            variant="outlined"
            value={data?.displayName || ""}
            onBlur={trimOnBlur(setData, "displayName")}
            onChange={(e) => setData({ ...data, displayName: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            value={data?.description || ""}
            onBlur={trimOnBlur(setData, "description")}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            helperText="Optional (e.g. Applications, Data and AI)"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="status"
            select
            label="Active"
            fullWidth
            variant="outlined"
            value={data?.status || "ACTIVE"}
            onChange={(e) => setData({ ...data, status: e.target.value })}
            helperText="*Required"
          >
            <MenuItem key="ACTIVE" value="ACTIVE">
              YES
            </MenuItem>
            <MenuItem key="DISABLED" value="DISABLED">
              No
            </MenuItem>
          </TextField>
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setMessageOpen(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveDepartmentInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddDepartmentDialog);
